// Import polyfills for Node.js 22+ compatibility
import "../polyfills";
import "@/lib/setupBrowserProxyAuth";
import React, { useRef, useState } from "react";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { SessionProvider } from "next-auth/react";
import "../styles/global.css";
import Head from "next/head";
import AzureAppInsights from "../components/AzureAppInsights/AzureAppInsights";
import "@/assets/fonts/fonts.css";
import { store } from "../redux/store";
import { Provider } from "react-redux";
import NotificationBanner from "../components/ChatLayout/NotificationBanner";

/** Client-only — avoids pulling antd `Modal` through SSR for every page (including `/home`). */
const ZscarPopup = dynamic(() => import("../components/HomeScreen/zscarpopup"), { ssr: false });
import { SiteDisabledOverlay } from "../components/SiteDisabled403/SiteDisabledOverlay";
import { useNotificationStore } from "../store/zustand/notificationStore";
import { TeamsAuthProvider } from "@/context/TeamsAuthContext";

export interface TourContextType {
  openTour: boolean;
  setOpenTour: React.Dispatch<React.SetStateAction<boolean>>;
  isLibraryModalVisible: boolean;
  setIsLibraryModalVisible: React.Dispatch<React.SetStateAction<boolean>>;

  newChatTourRef: React.RefObject<HTMLDivElement>;
  libraryTourRef: React.RefObject<HTMLDivElement>;
  sdlcAgentsTourRef: React.RefObject<HTMLDivElement>;
  termsMarNoteTourRef: React.RefObject<HTMLDivElement>;
  uploadDocTourRef: React.RefObject<HTMLDivElement>;
  spacesTourRef: React.RefObject<HTMLDivElement>;
  tredenceDisTourRef: React.RefObject<HTMLDivElement>;
  peopleSearchTourRef: React.RefObject<HTMLDivElement>;
  documentSearchTourRef: React.RefObject<HTMLDivElement>;
}


export const TourContext = React.createContext<TourContextType | undefined>(undefined);
const App = ({ Component, pageProps: { session, ...pageProps } }: AppProps) => {
  const notification = useNotificationStore();
  const [openTour, setOpenTour] = useState<boolean>(false);
  const [isLibraryModalVisible, setIsLibraryModalVisible] = useState<boolean>(false);
  const newChatTourRef = useRef<HTMLDivElement>(null);
  const libraryTourRef = useRef<HTMLDivElement>(null);
  const sdlcAgentsTourRef = useRef<HTMLDivElement>(null);
  const termsMarNoteTourRef = useRef<HTMLDivElement>(null);
  const uploadDocTourRef = useRef<HTMLDivElement>(null);
  const spacesTourRef = useRef<HTMLDivElement>(null);
  const tredenceDisTourRef = useRef<HTMLDivElement>(null);
  const peopleSearchTourRef = useRef<HTMLDivElement>(null);
  const documentSearchTourRef = useRef<HTMLDivElement>(null);

  return (
    <Provider store={store}>
      <SessionProvider session={session}>
        {/* TeamsAuthProvider must be inside SessionProvider — it calls useSession() internally */}
        <TeamsAuthProvider>
        <TourContext.Provider value={{
          newChatTourRef,
          libraryTourRef,
          sdlcAgentsTourRef,
          termsMarNoteTourRef,
          uploadDocTourRef,
          spacesTourRef,
          tredenceDisTourRef,
          peopleSearchTourRef,
          documentSearchTourRef,
          openTour,
          setOpenTour,
          isLibraryModalVisible,
          setIsLibraryModalVisible
        }}>
          <Head>
            <title>TReK2.0</title>
            <link rel="shortcut icon" href="/TRek_fav.ico" />
            <link rel="icon" type="image/x-icon" href="/TRek_fav.ico" />
          </Head>
          <AzureAppInsights>
            <Component {...pageProps} />
          </AzureAppInsights>

          <ZscarPopup />

          <SiteDisabledOverlay />

          {/* Global Notification Banner */}
          <NotificationBanner
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={notification.hideNotification}
            visible={notification.visible}
          />
        </TourContext.Provider>
        </TeamsAuthProvider>
      </SessionProvider>
    </Provider>

  );
};

export default App;
